'use client';

import {useActionState} from 'react';
import {usePathname, useRouter, useSearchParams} from 'next/navigation';

import {Breadcrumb} from '@/modules/foundation/components/breadcrumb';
import {Button} from '@/modules/foundation/components/button';
import {Container} from '@/modules/foundation/components/container';
import {Paragraph} from '@/modules/foundation/components/typography/paragraph';
import {PortableText} from '@/modules/foundation/sanity/portable-text';
import {TextHeading} from '@/modules/page/components/page-head/text-heading';

import {addToCart, updateCart} from '../../cart/actions';
import {FormattedItemPrice} from '../../item/FormattedItemPrice';
import type {ItemForPriceFragment} from '../../item/query';
import {
	productConfigurationFromSearchParams,
	searchParamsFromProductConfiguration,
} from '../configuration';
import {ProductImageCarousel} from '../ProductImageCarousel';
import {PRODUCT_PAGE_ALL_SLUG} from '../schema/constants';

import {
	getDefaultProductConfiguration,
	getItemsForProductConfiguration,
	isValidProductConfiguration,
	productConfigurationFromFormData,
} from './configuration';
import {ProductVariantSelector} from './ProductVariantSelector';
import type {ProductFragment} from './query';

export type ProductPageProps = {
	product: ProductFragment;
	shoppingDisabled?: boolean;
};

export default function ProductPage({product, shoppingDisabled}: ProductPageProps) {
	const searchParams = useSearchParams();
	const router = useRouter();
	const pathname = usePathname();
	const [addToCartState, addToCartAction, isAddingToCart] = useActionState(addToCart, {});
	const [updateCartState, updateCartAction, isUpdatingCart] = useActionState(updateCart, {});

	const cartLineId = searchParams.get('cartLineId') ?? null;

	const configuration =
		productConfigurationFromSearchParams(product, searchParams) ??
		getDefaultProductConfiguration(product);

	// Find selected items:
	const isValid = isValidProductConfiguration(product, configuration);
	let selectedItems: ItemForPriceFragment[] = [];

	if (isValid) {
		selectedItems = getItemsForProductConfiguration(product, configuration);
	}

	const category = product.category;
	const subcategory = product.subcategory;

	const productSlug = `${subcategory?.fullSlug}/${product.slug?.current}`;

	// FIXME: need to check out how to use product image with logo if it's available
	// FIXME: products needs to have description
	// const productData: WithContext<Product> = {
	// 	'@context': 'https://schema.org',
	// 	'@type': 'Product',
	// 	name: product.name,
	// 	brand: product.resolvedBrand?.name ?? undefined,
	// 	category: product.subcategory?.name ?? undefined,
	// 	offers: {
	// 		'@type': 'Offer',
	// 		price: priceIncVat.toString(),
	// 		priceCurrency: 'NOK',
	// 		itemCondition: 'https://schema.org/NewCondition',
	// 		url: `${siteUrl}/${productSlug}`,
	// 	},
	// };

	return (
		<>
			<TextHeading
				heading={product.name}
				breadcrumb={
					<Breadcrumb
						breadcrumb={[
							{
								href: `/${PRODUCT_PAGE_ALL_SLUG}`,
								title: 'Produkter',
							},
							{
								href: `/${PRODUCT_PAGE_ALL_SLUG}/${category?.slug}`,
								title: category?.name ?? '',
							},
							{
								href: `/${PRODUCT_PAGE_ALL_SLUG}/${subcategory?.fullSlug}`,
								title: subcategory?.name ?? '',
							},
							{
								href: `/${PRODUCT_PAGE_ALL_SLUG}/${productSlug}`,
								title: product.name ?? '',
								active: true,
							},
						]}
					/>
				}
			>
				<Paragraph size="large">{product.supplier?.name}</Paragraph>
			</TextHeading>
			<Container width="normal" spacingBottom="normal" spacingTop="small">
				<div className="md:flex md:flex-row md:items-start md:gap-8">
					{/*<JsonLd jsonLd={productData} />*/}

					<div className="mb-8 md:sticky md:top-8 md:w-1/2">
						<ProductImageCarousel product={product} config={configuration} />
					</div>

					<form
						className="md:w-1/2"
						action={cartLineId ? updateCartAction : addToCartAction}
						onChange={(e) => {
							// TODO: Debounce these events to avoid many consecutive reloads

							// Update the page search params with the new configuration:
							const formData = new FormData(e.currentTarget);
							const configuration = productConfigurationFromFormData(
								product,
								formData,
							);

							if (configuration) {
								const params = searchParamsFromProductConfiguration(configuration);
								if (cartLineId) {
									params.set('cartLineId', cartLineId);
								}

								router.replace(`${pathname}?${params.toString()}`, {
									scroll: false,
								});
							}
						}}
					>
						<input type="hidden" name="productId" defaultValue={product._id} />

						{product.description && (
							<div className="mb-8">
								<PortableText value={product.description} />
							</div>
						)}

						{product.variants && product.variants.length > 0 && (
							<div className="mb-8">
								<ProductVariantSelector
									variants={product.variants}
									defaultVariantId={configuration?.variantId ?? null}
								/>
							</div>
						)}

						{selectedItems && !!selectedItems.length && (
							<div className="text-primary font-display mb-8 text-4xl">
								<h2 className="sr-only">Pris</h2>
								<FormattedItemPrice items={selectedItems} />
							</div>
						)}
						{cartLineId?.length ? (
							<input
								name="cartLineId"
								defaultValue={cartLineId}
								className="sr-only"
							/>
						) : null}
						<Button
							type="submit"
							disabled={shoppingDisabled || isAddingToCart || isUpdatingCart}
							size="lg"
						>
							{isAddingToCart || isUpdatingCart
								? cartLineId
									? 'Oppdaterer produkt i handlevogn'
									: 'Legger til i handlekurv...'
								: cartLineId
									? 'Oppdater produkt i handlevogn'
									: 'Legg i handlekurv'}
						</Button>
						{shoppingDisabled && (
							<Paragraph>
								Netthandel er midlertidig deaktivert. Sjekk tilbake senere.
							</Paragraph>
						)}
						{addToCartState.error && <Paragraph>{addToCartState.error}</Paragraph>}
						{updateCartState.error && <Paragraph>{updateCartState.error}</Paragraph>}
					</form>
				</div>
			</Container>
		</>
	);
}
